import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Check, X, Clock, Trash2, Power, PowerOff, RefreshCw, ShieldCheck, Edit3, ImageOff, Save } from 'lucide-react';

/**
 * Admin - Experiences tab.
 *
 * Opens/closes the 72-hour window and moderates incoming reports:
 * approve (assigns next public number), reject, or delete permanently.
 * Shows the submitter's Instagram handle (private only to admin).
 */

const API = process.env.REACT_APP_BACKEND_URL;

function shortTime(iso) {
  if (!iso) return '-';
  return iso.replace('T', ' ').split('.')[0].replace('+00:00', '') + ' UTC';
}

const StatusBadge = ({ status, isConfirmed }) => {
  if (status === 'approved') {
    return (
      <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${
        isConfirmed
          ? 'bg-emerald-950/50 text-emerald-300 border-emerald-900/50'
          : 'bg-blue-950/40 text-blue-300 border-blue-900/50'
      }`}>
        {isConfirmed ? '✓ Confirmed' : 'Approved'}
      </span>
    );
  }
  if (status === 'rejected') {
    return <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded bg-zinc-800 text-zinc-500 border border-zinc-700">Rejected</span>;
  }
  return <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded bg-yellow-950/40 text-yellow-300 border border-yellow-900/50">Pending</span>;
};

const ExperiencesTab = ({ token }) => {
  const [window_, setWindow] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // Report currently open in the edit modal (null when closed).
  const [editing, setEditing] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const authFetch = useCallback(
    (path, init = {}) =>
      fetch(`${API}${path}`, {
        ...init,
        headers: { ...(init.headers || {}), Authorization: `Bearer ${token}` },
      }),
    [token],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [wRes, iRes] = await Promise.all([
        authFetch('/api/admin/experiences/window'),
        authFetch('/api/admin/experiences'),
      ]);
      if (wRes.ok) setWindow(await wRes.json());
      if (iRes.ok) setItems((await iRes.json()).items || []);
    } catch {
      toast.error('Could not load experiences data');
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const toggleWindow = async (activate) => {
    setBusy(true);
    try {
      const res = await authFetch('/api/admin/experiences/window', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: activate }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setWindow(data);
      toast.success(activate ? '72-hour window opened' : 'Window closed');
    } catch {
      toast.error('Could not update window');
    } finally {
      setBusy(false);
    }
  };

  const approve = async (id) => {
    try {
      const res = await authFetch(`/api/admin/experiences/${id}/approve`, { method: 'POST' });
      if (!res.ok) throw new Error();
      toast.success('Approved');
      load();
    } catch { toast.error('Approve failed'); }
  };
  const reject = async (id) => {
    if (!window.confirm('Reject this report? It will be hidden from the public list.')) return;
    try {
      const res = await authFetch(`/api/admin/experiences/${id}/reject`, { method: 'POST' });
      if (!res.ok) throw new Error();
      toast.success('Rejected');
      load();
    } catch { toast.error('Reject failed'); }
  };
  const remove = async (id) => {
    if (!window.confirm('Permanently delete this report? This cannot be undone.')) return;
    try {
      const res = await authFetch(`/api/admin/experiences/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error();
      toast.success('Deleted');
      load();
    } catch { toast.error('Delete failed'); }
  };

  // ------- Edit modal handlers -------
  const saveEdit = async (patch) => {
    if (!editing) return;
    setSavingEdit(true);
    try {
      const res = await authFetch(`/api/admin/experiences/${editing.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error();
      toast.success('Saved');
      setEditing(null);
      load();
    } catch { toast.error('Save failed'); }
    finally { setSavingEdit(false); }
  };

  const uploadMedia = async (file) => {
    const presign = await authFetch('/api/experiences/presign', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename: file.name, content_type: file.type }),
    });
    if (!presign.ok) throw new Error('Presign failed');
    const { upload_url, public_url } = await presign.json();
    const put = await fetch(upload_url, {
      method: 'PUT', headers: { 'Content-Type': file.type }, body: file,
    });
    if (!put.ok) throw new Error('Upload failed');
    return public_url;
  };

  const pending = items.filter((i) => i.status === 'pending');
  const approved = items.filter((i) => i.status === 'approved');
  const rejected = items.filter((i) => i.status === 'rejected');

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6" data-testid="experiences-tab">
      {/* Window control */}
      <div className={`border rounded-lg p-5 ${window_?.section_enabled === false ? 'bg-red-950/30 border-red-900/40' : 'bg-zinc-900/40 border-zinc-800'} mb-4`}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className={`text-xs font-bold uppercase tracking-widest mb-1 ${window_?.section_enabled === false ? 'text-red-400' : 'text-zinc-400'}`}>
              Site-Wide Section Visibility
            </p>
            <p className="text-white font-bold text-lg">
              {window_?.section_enabled === false ? 'HIDDEN from the public site' : 'VISIBLE on the public site'}
            </p>
            <p className="text-zinc-400 text-xs mt-1">
              {window_?.section_enabled === false
                ? 'Nav link + public pages are turned off. Submissions still viewable here.'
                : 'The Madame Files nav link and public pages are live.'}
            </p>
          </div>
          <button
            type="button"
            onClick={async () => {
              const nextEnabled = !(window_?.section_enabled !== false);
              setBusy(true);
              try {
                const res = await authFetch('/api/admin/experiences/section', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ enabled: nextEnabled }),
                });
                if (!res.ok) throw new Error();
                setWindow(await res.json());
                toast.success(nextEnabled ? 'Section is now visible' : 'Section is hidden from the public');
              } catch { toast.error('Could not toggle section'); }
              finally { setBusy(false); }
            }}
            disabled={busy}
            data-testid="experiences-section-toggle"
            className={`font-bold px-4 py-2 rounded flex items-center gap-2 text-sm ${
              window_?.section_enabled === false
                ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                : 'bg-red-600 hover:bg-red-700 text-white'
            }`}
          >
            {window_?.section_enabled === false ? 'Turn section ON' : 'Turn section OFF'}
          </button>
        </div>
      </div>

      <div className={`border rounded-lg p-5 ${window_?.is_open ? 'bg-emerald-950/30 border-emerald-900/40' : 'bg-zinc-900/40 border-zinc-800'}`}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className={`text-xs font-bold uppercase tracking-widest mb-1 ${window_?.is_open ? 'text-emerald-400' : 'text-zinc-500'}`}>
              <Clock className="inline h-3.5 w-3.5 mr-1" />
              72-Hour Reporting Window
            </p>
            <p className="text-white font-bold text-lg">
              {window_?.is_open
                ? `OPEN - ${window_.hours_remaining?.toFixed(1)}h remaining`
                : 'Closed'}
            </p>
            <p className="text-zinc-400 text-xs mt-1">
              {window_?.is_open
                ? `Opened at ${shortTime(window_.opened_at)}`
                : 'Open the window to start accepting submissions.'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              disabled={loading}
              className="text-zinc-400 hover:text-white p-2"
              title="Refresh"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            {window_?.is_open ? (
              <button
                onClick={() => toggleWindow(false)}
                disabled={busy}
                className="bg-zinc-800 hover:bg-zinc-700 text-red-400 font-bold px-4 py-2 rounded flex items-center gap-2 text-sm"
                data-testid="experiences-close-btn"
              >
                <PowerOff className="h-4 w-4" /> Close window
              </button>
            ) : (
              <button
                onClick={() => toggleWindow(true)}
                disabled={busy}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-4 py-2 rounded flex items-center gap-2 text-sm"
                data-testid="experiences-open-btn"
              >
                <Power className="h-4 w-4" /> Open 72-hour window
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Pending', value: pending.length, color: 'text-yellow-300' },
          { label: 'Approved', value: approved.length, color: 'text-emerald-300' },
          { label: 'Rejected', value: rejected.length, color: 'text-zinc-500' },
        ].map((s) => (
          <div key={s.label} className="bg-zinc-900/40 border border-zinc-800 rounded p-4">
            <p className="text-zinc-500 text-[10px] uppercase tracking-widest font-bold">{s.label}</p>
            <p className={`${s.color} font-black text-2xl mt-1`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Pending list */}
      <div>
        <h3 className="text-white font-bold text-lg mb-3">Pending Review ({pending.length})</h3>
        {pending.length === 0 && (
          <p className="text-zinc-500 text-sm italic mb-6">Nothing awaiting review.</p>
        )}
        <div className="space-y-3">
          {pending.map((r) => (
            <div key={r.id} className="bg-yellow-950/10 border border-yellow-900/40 rounded p-4" data-testid={`experiences-pending-${r.id}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <p className="text-white text-sm font-mono">
                    {r.when_iso || 'no date'} · {r.location}
                  </p>
                  <p className="text-zinc-500 text-[11px] mt-0.5">Submitted {shortTime(r.submitted_at)}</p>
                </div>
                <StatusBadge status={r.status} isConfirmed={r.is_confirmed} />
              </div>
              <p className="text-zinc-200 text-sm whitespace-pre-wrap mb-3">{r.description}</p>
              {r.instagram_handle && (
                <p className="text-emerald-400 text-xs mb-3">
                  <ShieldCheck className="inline h-3 w-3 mr-1" />
                  IG: <a href={`https://instagram.com/${r.instagram_handle}`} target="_blank" rel="noopener noreferrer" className="underline">@{r.instagram_handle}</a>
                </p>
              )}
              {r.media_urls && r.media_urls.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-3">
                  {r.media_urls.map((url, i) => {
                    const isVideo = /\.(mp4|mov|webm)(\?|$)/i.test(url);
                    return isVideo ? (
                      <video key={i} src={url} controls className="rounded bg-black w-full h-24 object-cover" />
                    ) : (
                      <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                        <img src={url} alt="" loading="lazy" className="rounded bg-black w-full h-24 object-cover" />
                      </a>
                    );
                  })}
                </div>
              )}
              <p className="text-zinc-600 text-[10px] font-mono mb-3">
                IP: {r.ip || '-'} · UA: {(r.user_agent || '').slice(0, 60)}
              </p>
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => approve(r.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3 py-1.5 rounded flex items-center gap-1" data-testid={`experiences-approve-${r.id}`}>
                  <Check className="h-3.5 w-3.5" /> Approve & publish
                </button>
                <button onClick={() => setEditing(r)} className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold px-3 py-1.5 rounded flex items-center gap-1" data-testid={`experiences-edit-${r.id}`}>
                  <Edit3 className="h-3.5 w-3.5" /> Edit
                </button>
                <button onClick={() => reject(r.id)} className="bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs font-bold px-3 py-1.5 rounded flex items-center gap-1" data-testid={`experiences-reject-${r.id}`}>
                  <X className="h-3.5 w-3.5" /> Reject
                </button>
                <button onClick={() => remove(r.id)} className="ml-auto text-zinc-500 hover:text-red-400 text-xs flex items-center gap-1">
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Approved list */}
      <div>
        <h3 className="text-white font-bold text-lg mb-3">Published ({approved.length})</h3>
        <div className="space-y-2">
          {approved.map((r) => (
            <div key={r.id} className="bg-zinc-900/30 border border-zinc-800 rounded p-3 flex items-center justify-between gap-3" data-testid={`experiences-approved-${r.id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-white text-sm">
                  <span className="text-red-400 font-bold">#{r.public_number}</span> · {r.location}
                  {r.instagram_handle && <span className="text-emerald-400 ml-2 text-xs">✓ @{r.instagram_handle}</span>}
                </p>
                <p className="text-zinc-500 text-xs truncate">{r.description}</p>
              </div>
              <StatusBadge status={r.status} isConfirmed={r.is_confirmed} />
              <button onClick={() => setEditing(r)} className="text-zinc-500 hover:text-blue-400" title="Edit"><Edit3 className="h-4 w-4" /></button>
              <button onClick={() => remove(r.id)} className="text-zinc-500 hover:text-red-400" title="Delete">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Rejected (compact) */}
      {rejected.length > 0 && (
        <details className="text-zinc-500">
          <summary className="cursor-pointer text-sm">Rejected ({rejected.length})</summary>
          <div className="mt-2 space-y-1">
            {rejected.map((r) => (
              <div key={r.id} className="flex items-center justify-between text-xs bg-zinc-950 rounded px-3 py-2">
                <span className="truncate">{r.location} · {(r.description || '').slice(0, 80)}…</span>
                <div className="flex gap-2">
                  <button onClick={() => setEditing(r)} className="text-zinc-600 hover:text-blue-400"><Edit3 className="h-3.5 w-3.5" /></button>
                  <button onClick={() => remove(r.id)} className="text-zinc-600 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
        </details>
      )}

      {editing && (
        <EditModal
          report={editing}
          onClose={() => setEditing(null)}
          onSave={saveEdit}
          onUpload={uploadMedia}
          saving={savingEdit}
        />
      )}
    </div>
  );
};

/**
 * Modal to edit an existing experience report - text fields + media
 * add/remove + the "publish media" toggle. Media list is edited locally
 * then flushed to the backend on Save.
 */
const EditModal = ({ report, onClose, onSave, onUpload, saving }) => {
  const [when_iso, setWhen] = useState(report.when_iso || '');
  const [location, setLocation] = useState(report.location || '');
  const [description, setDescription] = useState(report.description || '');
  const [media_urls, setMediaUrls] = useState(report.media_urls || []);
  const [publish_media, setPublishMedia] = useState(!!report.publish_media);
  const [uploading, setUploading] = useState(false);

  const removeUrl = (idx) => setMediaUrls((prev) => prev.filter((_, i) => i !== idx));

  const handleAdd = async (e) => {
    const list = Array.from(e.target.files || []).slice(0, 8 - media_urls.length);
    if (!list.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of list) uploaded.push(await onUpload(f));
      setMediaUrls((prev) => [...prev, ...uploaded]);
      toast.success(`Added ${uploaded.length} file${uploaded.length === 1 ? '' : 's'}`);
    } catch (err) { toast.error(err.message || 'Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-[#0d0d0f] border border-zinc-800 rounded-lg w-full max-w-2xl my-8 p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="experiences-edit-modal">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-red-500 text-[10px] font-black uppercase tracking-widest">Edit report</p>
            <h3 className="text-white font-bold text-lg">
              {report.public_number ? `#${report.public_number} · ` : ''}{report.location || 'Untitled'}
            </h3>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="h-5 w-5" /></button>
        </div>

        <div>
          <label className="block text-white text-xs font-bold uppercase tracking-wider mb-1">When</label>
          <input type="date" value={when_iso} onChange={(e) => setWhen(e.target.value)}
            className="w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-white text-sm focus:border-red-500 focus:outline-none" />
        </div>
        <div>
          <label className="block text-white text-xs font-bold uppercase tracking-wider mb-1">Where</label>
          <input type="text" value={location} onChange={(e) => setLocation(e.target.value)} maxLength={200}
            className="w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-white text-sm focus:border-red-500 focus:outline-none" />
        </div>
        <div>
          <label className="block text-white text-xs font-bold uppercase tracking-wider mb-1">Description</label>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={8} maxLength={6000}
            className="w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-white text-sm focus:border-red-500 focus:outline-none resize-y" />
          <p className="text-zinc-600 text-[10px] mt-1">{description.length} / 6000</p>
        </div>

        <div>
          <label className="block text-white text-xs font-bold uppercase tracking-wider mb-2">Media ({media_urls.length}/8)</label>
          {media_urls.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-3">
              {media_urls.map((url, i) => {
                const isVideo = /\.(mp4|mov|webm)(\?|$)/i.test(url);
                return (
                  <div key={i} className="relative group">
                    {isVideo
                      ? <video src={url} className="rounded bg-black w-full h-24 object-cover" muted />
                      : <img src={url} alt="" className="rounded bg-black w-full h-24 object-cover" />}
                    <button
                      type="button"
                      onClick={() => removeUrl(i)}
                      className="absolute top-1 right-1 bg-red-600 hover:bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Remove"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          {media_urls.length < 8 && (
            <label className="block cursor-pointer bg-zinc-950 border border-zinc-800 border-dashed hover:border-red-500/60 rounded p-3 text-center text-zinc-500 text-xs">
              {uploading ? 'Uploading…' : '+ Add photo/video'}
              <input type="file" multiple accept="image/*,video/*" onChange={handleAdd} className="hidden" disabled={uploading} />
            </label>
          )}
          <label className="mt-3 flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={publish_media} onChange={(e) => setPublishMedia(e.target.checked)} className="mt-0.5 accent-red-500 h-4 w-4" />
            <span className="text-zinc-300 text-xs">
              <span className="font-bold text-white">Publish media alongside this report.</span>
              <span className="block text-zinc-500 mt-0.5">Off = admin-only. On = shown on the public page.</span>
            </span>
          </label>
        </div>

        <div className="flex gap-2 pt-3 border-t border-zinc-900">
          <button
            type="button"
            onClick={() => onSave({ when_iso, location, description, media_urls, publish_media })}
            disabled={saving || uploading}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:bg-zinc-800 text-white font-bold py-2 rounded text-sm flex items-center justify-center gap-2"
          >
            <Save className="h-4 w-4" /> {saving ? 'Saving…' : 'Save changes'}
          </button>
          <button type="button" onClick={onClose} className="bg-zinc-800 hover:bg-zinc-700 text-white font-bold py-2 px-4 rounded text-sm">Cancel</button>
        </div>
      </div>
    </div>
  );
};

export default ExperiencesTab;
